/**
 * 视频播放优化脚本 - 简洁版本
 * 功能：当视频不在视口内时暂停播放，进入视口时恢复播放
 */

(function () {
  'use strict';

  // 使用 WeakMap 存储视频状态，自动处理内存管理
  const videoStates = new WeakMap();
  let observer = null;

  // 正确的节流函数
  function throttle(func, delay) {
    let lastTime = 0;
    return function (...args) {
      const now = Date.now();
      if (now - lastTime >= delay) {
        lastTime = now;
        func.apply(this, args);
      }
    };
  }

  // 备用方案：检查视频是否在视口内
  function isInViewport(video) {
    const rect = video.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;

    // 计算可见区域比例
    const visibleArea = Math.max(0, Math.min(vh, rect.bottom) - Math.max(0, rect.top)) *
      Math.max(0, Math.min(vw, rect.right) - Math.max(0, rect.left));
    const totalArea = rect.width * rect.height;

    return totalArea > 0 && (visibleArea / totalArea) >= 0.2;
  }

  // 处理视频可见性变化
  function handleVideoVisibility(video, isVisible) {
    if (video.hasAttribute('data-no-optimization')) return;

    let state = videoStates.get(video);
    if (!state) {
      state = { wasPlaying: false, currentTime: 0 };
      videoStates.set(video, state);
    }

    if (isVisible) {
      // 进入视口，恢复播放
      if (state.wasPlaying && video.paused) {
        video.currentTime = state.currentTime;
        video.play().catch(() => {
          // 播放失败，静默处理
        });
        state.wasPlaying = false;
      }
    } else {
      // 离开视口，暂停播放
      if (!video.paused) {
        state.wasPlaying = true;
        state.currentTime = video.currentTime;
        video.pause();
      }
    }
  }

  // 创建 Intersection Observer
  function createObserver() {
    if (!('IntersectionObserver' in window)) return null;

    return new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const isVisible = entry.isIntersecting && entry.intersectionRatio >= 0.2;
        handleVideoVisibility(entry.target, isVisible);
      });
    }, {
      threshold: [0, 0.2, 1]
    });
  }

  // 处理所有视频（备用方案）
  function handleAllVideos() {
    document.querySelectorAll('video').forEach(video => {
      handleVideoVisibility(video, isInViewport(video));
    });
  }

  // 初始化
  function init() {
    observer = createObserver();

    if (observer) {
      // 使用 Intersection Observer
      document.querySelectorAll('video').forEach(video => {
        observer.observe(video);
      });
    } else {
      // 备用方案：滚动监听
      const throttledHandle = throttle(handleAllVideos, 100);
      window.addEventListener('scroll', throttledHandle, { passive: true });
      window.addEventListener('resize', throttledHandle, { passive: true });
      handleAllVideos();
    }

    // 页面隐藏时暂停所有视频
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        document.querySelectorAll('video').forEach(video => {
          if (!video.paused && !video.hasAttribute('data-no-optimization')) {
            let state = videoStates.get(video);
            if (!state) {
              state = { wasPlaying: false, currentTime: 0 };
              videoStates.set(video, state);
            }
            state.wasPlaying = true;
            state.currentTime = video.currentTime;
            video.pause();
          }
        });
      } else {
        // 页面恢复可见时重新检查
        setTimeout(() => {
          if (observer) {
            // 重新观察所有视频（Intersection Observer 会自动处理重复观察）
            document.querySelectorAll('video').forEach(video => {
              observer.observe(video);
            });
          } else {
            handleAllVideos();
          }
        }, 100);
      }
    });
  }

  // 页面加载完成后初始化
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // 简单的公共API
  window.VideoOptimization = {
    refresh: () => {
      if (observer) {
        document.querySelectorAll('video').forEach(video => {
          observer.observe(video);
        });
      } else {
        handleAllVideos();
      }
    }
  };
})();